// Notifications Hook - AgentBheem ERP
// Returns mock data when backend is unavailable
// Note: This hook is for ss-client-management notifications, NOT PM notifications
// PM module has its own notification system in src/modules/pm/hooks/useNotifications.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useLocation } from 'react-router-dom'
import { notificationService, type NotificationListResponse } from '@/services/notificationService'

// Mock data for when backend is unavailable
const mockNotifications: NotificationListResponse = {
  items: [],
  total: 0,
  page: 1,
  page_size: 20,
  total_pages: 0,
}

/**
 * Check if current path is in PM module
 * PM module has its own notification system, so we skip ss-client-management calls
 */
function useIsInPMModule(): boolean {
  try {
    const location = useLocation()
    return location.pathname.includes('/pm') || location.pathname.includes('/project-management')
  } catch {
    // useLocation might fail if not in Router context
    return false
  }
}

export function useNotifications(params?: {
  page?: number
  page_size?: number
  notification_type?: string
  status?: string
}) {
  const isInPM = useIsInPMModule()

  return useQuery({
    queryKey: ['notifications', params],
    queryFn: async () => {
      // Skip API call if in PM module (PM has its own notifications)
      if (isInPM) {
        return mockNotifications
      }
      try {
        return await notificationService.getNotifications(params)
      } catch {
        // Return empty data when API is unavailable
        return mockNotifications
      }
    },
    staleTime: 5 * 60_000, // 5 minutes (less aggressive)
    refetchInterval: false, // Don't auto-refetch
    retry: 0, // Don't retry - reduces 403 noise
    enabled: !isInPM, // Disable query entirely in PM module
  })
}

export function useUnreadNotifications() {
  const isInPM = useIsInPMModule()

  return useQuery({
    queryKey: ['notifications', 'unread'],
    queryFn: async () => {
      // Skip API call if in PM module (PM has its own notifications)
      if (isInPM) {
        return mockNotifications
      }
      try {
        return await notificationService.getNotifications({
          page: 1,
          page_size: 10,
          status: 'delivered', // Not yet read
        })
      } catch {
        // Return empty data when API is unavailable
        return mockNotifications
      }
    },
    staleTime: 5 * 60_000, // 5 minutes
    refetchInterval: false, // Don't auto-refetch
    retry: 0, // Don't retry - reduces 403 noise
    enabled: !isInPM, // Disable query entirely in PM module
  })
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (notificationId: string) => notificationService.markAsRead(notificationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
  })
}

export function useNotificationKPIs(days = 30) {
  return useQuery({
    queryKey: ['notifications', 'kpis', days],
    queryFn: async () => {
      try {
        return await notificationService.getKPIs(days)
      } catch {
        // Return mock KPIs when API is unavailable
        return {
          total_sent: 0,
          pending: 0,
          delivered: 0,
          failed: 0,
          bounced: 0,
          delivery_rate: 0,
          by_channel: {},
          by_type: {},
        }
      }
    },
    staleTime: 5 * 60_000, // 5 minutes
    retry: 1,
  })
}
